import { Immutable } from "immer";
import React from "react";

import type { LifestyleModule as Value } from "@reactivated";

// NOTE: This component reproduces the legacy server-rendered markup from
// `cms/product_display_page.html` (the `block_type == 'lifestyle'` branch)
// verbatim — every classname is load-bearing for the existing SCSS, so keep
// them in sync. The `pdp-lifestyle pdp-section  l-full-width` root class string
// intentionally contains a double space to match the legacy template.
//
// `contentTitleTag` selects the element used for the two
// `.pdp-lifestyle__content__title` nodes (the mobile/header title and each
// tile title). Legacy `product_display_page.html` rendered them as `<h3>`
// (lines 76 & 87 at b767b34), while `generic_page.html` rendered them as
// `<div>` (lines 32 & 43 at b767b34). Defaults to the PDP `h3` variant so PDP
// (and every other shared-map consumer) stays byte-identical; the generic-page
// assembly passes `"div"` to restore that page's legacy heading outline.
type IProps = Immutable<{
    value: Value;
    contentTitleTag?: "h3" | "div";
}>;

type LifestyleTile = NonNullable<Value["lifestyle_tile"]>[number];

const backgroundImageStyle = (
    image: Immutable<Value["lifestyle_top_image"]> | LifestyleTile["image"],
): React.CSSProperties | undefined => {
    // Legacy emitted `style="background-image: url('<url>');"`. When the image
    // is null the legacy markup rendered `url('')` (a broken url); we instead
    // omit the inline style entirely while keeping the wrapper element. See the
    // ComparisonCarouselBlock precedent (renderProductImage returns null).
    if (!image) {
        return undefined;
    }
    return { backgroundImage: `url('${image.url}')` };
};

export const LifestyleModule = (props: IProps): React.JSX.Element => {
    const v = props.value;
    // Only the *tag* of `.pdp-lifestyle__content__title` differs between the
    // PDP (`h3`) and generic-page (`div`) variants; the `|safe` / escaping
    // behavior is identical, so the content mechanisms below are unchanged.
    const ContentTitleTag = props.contentTitleTag ?? "h3";
    // content_alignment / gradient_option are interpolated directly into
    // classnames in the legacy template; Django renders `None` as an empty
    // string, producing a trailing dash (e.g. `topper-callout__overlay--`).
    // Match that with `?? ""` rather than letting `null` stringify.
    const alignment = v.content_alignment ?? "";
    const gradient = v.gradient_option ?? "";
    const tiles = v.lifestyle_tile ?? [];

    return (
        <div className="pdp-lifestyle pdp-section  l-full-width">
            <div
                className="pdp-lifestyle__header"
                style={backgroundImageStyle(v.lifestyle_top_image)}
            >
                <div
                    className={`topper-callout__overlay topper-callout__overlay--${alignment} topper-callout__overlay--${gradient}`}
                ></div>
                <div
                    className={`l-capped-width pdp-lifestyle__header__content-container pdp-lifestyle__header__content-container--${alignment}`}
                >
                    <div className="pdp-lifestyle__header__content">
                        <h2
                            className="pdp-lifestyle__header__title pdp-section__title"
                            dangerouslySetInnerHTML={{
                                __html: v.lifestyle_title ?? "",
                            }}
                        />
                        <div
                            className="pdp-lifestyle__header__desc pdp-section__desc"
                            dangerouslySetInnerHTML={{
                                __html: v.lifestyle_description ?? "",
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className="pdp-lifestyle__content pdp-lifestyle__content--mobile-header l-capped-width">
                <div className="pdp-lifestyle__content__item">
                    <div
                        className="pdp-lifestyle__content__image"
                        style={backgroundImageStyle(v.lifestyle_top_image)}
                    ></div>
                    <div className="pdp-lifestyle__content__copy pdp-lifestyle__content__copy--header">
                        <ContentTitleTag
                            className="pdp-lifestyle__content__title"
                            dangerouslySetInnerHTML={{
                                __html: v.lifestyle_title ?? "",
                            }}
                        />
                        <div
                            className="pdp-lifestyle__content__desc pdp-section__desc"
                            dangerouslySetInnerHTML={{
                                __html: v.lifestyle_description ?? "",
                            }}
                        />
                    </div>
                </div>
            </div>
            {tiles.length > 0 && (
                <div className="pdp-lifestyle__content l-capped-width">
                    {tiles.map((tile, index) => {
                        // {% cycle 'odd' 'even' %} starts on 'odd' for the first
                        // (index 0) item.
                        const parity = index % 2 === 0 ? "odd" : "even";
                        const isLast = index === tiles.length - 1;
                        const itemClass = `pdp-lifestyle__content__item pdp-lifestyle__content__item--${parity}${
                            isLast
                                ? "  pdp-lifestyle__content__item--last "
                                : ""
                        }`;
                        return (
                            <div key={index} className={itemClass}>
                                <div
                                    className="pdp-lifestyle__content__image"
                                    style={backgroundImageStyle(tile.image)}
                                ></div>
                                <div className="pdp-lifestyle__content__copy">
                                    {/* tile title/desc are NOT `|safe` in the
                                        legacy template — render as text so React
                                        escaping matches Django autoescape. */}
                                    <ContentTitleTag className="pdp-lifestyle__content__title">
                                        {tile.title}
                                    </ContentTitleTag>
                                    <div className="pdp-lifestyle__content__desc pdp-section__desc">
                                        {tile.desc}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <div className="pdp-lifestyle__mobile-footer">
                        <a
                            className="pdp-lifestyle__button button button--ming"
                            href="#"
                        >
                            <span>SHOP NOW</span>
                        </a>
                        <hr className="pdp-lifestyle__divider" />
                    </div>
                </div>
            )}
        </div>
    );
};
